// Types for unified chunked upload system

import { ACCEPTED_FILE_TYPES } from "src/constants";
import type { UploadStatus } from "./fileUploadWithProgress";

// Upload contexts
export type UploadContext =
  | "dialogue"
  | "dataset"
  | "project"
  | "temp-tabular"
  | "project-genius"
  | "timeseries-stage"
  | "af-import"
  | "project-folder";

// Response types for unified chunked upload API
export interface ChunkedUploadResponse {
  status: "in_progress" | "completed";
  upload_id: string;
  chunks_received: number;
  total_chunks: number;
  progress: number; // 0.0 to 1.0
  result?: any; // Result object when completed
}

export interface ChunkedUploadStatus {
  status: "in_progress" | "completed";
  upload_id: string;
  chunks_received: number;
  total_chunks: number;
  progress: number;
  result?: any;
  context?: UploadContext;
  context_id?: string;
  filename?: string;
  created_at?: string;
  updated_at?: string;
}

// Upload options
export interface UnifiedUploadOptions {
  context: UploadContext;
  contextId: string;
  file: File;
  relativePath?: string;
  onProgress?: (progress: number) => void;
  onChunkProgress?: (
    chunkIndex: number,
    isUploaded: boolean,
    error?: string,
  ) => void;
  onTotalChunks?: (totalChunks: number) => void;
  onUploadIdReceived?: (uploadId: string) => void;
  onComplete?: (result: any) => void;
  onError?: (error: string) => void;
  chunkSize?: number;
  /**
   * Form fields sent with every request of the upload, chunked or not.
   * Names must not reuse a chunk protocol field (chunk_index, total_chunks,
   * filename, chunk_file, upload_id).
   */
  extraFields?: Record<string, string>;
}

// File with metadata
export interface UnifiedFileWithPreview extends File {
  preview?: string;
  sizeFormatted?: string;
  name: string;
  id?: number | string;
  isUploading?: boolean;
  uploadProgress?: number; // 0-100 for chunked upload progress
  chunkProgress?: ChunkUploadProgress[];
  totalChunks?: number;
  uploadedChunks?: number;
  uploadId?: string; // Server-provided upload session ID
  context?: UploadContext;
  error?: string;
}

export interface ChunkUploadProgress {
  chunkIndex: number;
  chunkHash: string; // Unique hash for the chunk
  isUploaded: boolean;
  error?: string;
}

// Legacy types for backwards compatibility - to be phased out
export interface ChunkedUploadSession {
  dialogueId: number;
  filename: string;
  totalChunks: number;
  uploadedChunks: number;
  isComplete: boolean;
}

export interface FileUploadProgress {
  filename: string;
  totalChunks: number;
  uploadedChunks: number;
  completionStatus: boolean;
  progress: number; // 0-100
  error?: string;
  uploadId?: string; // New field for tracking upload session
}

export interface DialogueUploadProgress {
  [filename: string]: FileUploadProgress;
}

// Legacy file type - use UnifiedFileWithPreview instead
export interface ChunkedFileWithPreview extends File {
  preview?: string;
  sizeFormatted?: string;
  name: string;
  id?: number | string;
  isUploading?: boolean;
  uploadProgress?: number; // 0-100 for chunked upload progress
  chunkProgress?: ChunkUploadProgress[];
  totalChunks?: number;
  uploadedChunks?: number;
  num_sdr?: number;
  num_sdr_not_embedded?: number;
  embeddingProgress?: number;
  uniqueId?: string; // Unique identifier for tracking file through upload process
  uploadId?: string; // Server-provided upload session ID
  uploadStatus?: UploadStatus; // Status tracking for UI
  error?: string; // Failure message from the upload hook
}

// Configuration for chunked uploads
export const MAX_RETRIES = 3;
export const RETRY_DELAY = 1000; // 1 second
export const RATE_LIMIT_RETRY_DELAY = 5000; // 5 seconds for rate limit retries

// Error types
export interface ChunkedUploadError {
  code:
    | "VALIDATION_ERROR"
    | "ACCESS_DENIED"
    | "NOT_FOUND"
    | "SESSION_EXPIRED"
    | "INTERNAL_ERROR";
  message: string;
  details?: any;
}

// Upload queue item
export interface UploadQueueItem {
  id: string;
  file: File;
  context: UploadContext;
  contextId: string;
  priority?: number;
  status: "pending" | "uploading" | "completed" | "failed" | "cancelled";
  progress?: number;
  uploadId?: string;
  error?: string;
  retryCount?: number;
}

// Context-specific configurations
export interface ContextConfig {
  maxFileSize?: number;
  allowedExtensions?: string[];
  chunkSize?: number;
  autoRetry?: boolean;
  maxRetries?: number;
}

export interface UploadConfigs {
  dialogue?: ContextConfig;
  dataset?: ContextConfig;
  project?: ContextConfig;
  "temp-tabular"?: ContextConfig;
  "project-genius"?: ContextConfig;
  "timeseries-stage"?: ContextConfig;
  "af-import"?: ContextConfig;
  "project-folder"?: ContextConfig;
}

// Default configurations
export const DEFAULT_UPLOAD_CONFIGS: UploadConfigs = {
  dialogue: {
    maxFileSize: undefined, // No size restriction
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT,
    chunkSize: 5 * 1024 * 1024, // 5MB
    autoRetry: true,
    maxRetries: 3,
  },
  dataset: {
    maxFileSize: 500 * 1024 * 1024, // 500MB
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.DATASET,
    chunkSize: 10 * 1024 * 1024, // 10MB
    autoRetry: true,
    maxRetries: 3,
  },
  // Atom Assist Projects
  project: {
    maxFileSize: undefined, // No size restriction
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.PROJECT_FOLDER,
    chunkSize: 5 * 1024 * 1024, // 5MB
    autoRetry: true,
    maxRetries: 3,
  },
  "project-genius": {
    maxFileSize: 500 * 1024 * 1024, // 500MB
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.PROJECT_XER,
    chunkSize: 5 * 1024 * 1024, // 5MB
    autoRetry: true,
    maxRetries: 3,
  },
  "temp-tabular": {
    maxFileSize: 1024 * 1024 * 1024, // 1GB for large CSV files
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.CSV, // Only text-based files can be chunked
    chunkSize: 10 * 1024 * 1024, // 10MB chunks for better performance
    autoRetry: true,
    maxRetries: 3,
    // Note: XLSX/XLS files fall back to original upload endpoint
  },
  "timeseries-stage": {
    // PI/Maximo CSV exports run 80-90 MB per unit; cap at 1 GB to leave
    // headroom for yearly-historian dumps without forcing a manual
    // split.
    maxFileSize: 1024 * 1024 * 1024,
    allowedExtensions: ACCEPTED_FILE_TYPES.EXTENSIONS.CSV,
    chunkSize: 10 * 1024 * 1024, // 10MB
    autoRetry: true,
    maxRetries: 3,
  },
  "af-import": {
    // PI Asset Framework XML exports can run very large for a full plant
    // hierarchy; cap at 1 GB and chunk so they don't ride one proxy request.
    maxFileSize: 1024 * 1024 * 1024,
    allowedExtensions: [".xml"],
    chunkSize: 10 * 1024 * 1024, // 10MB
    autoRetry: true,
    maxRetries: 3,
  },
};
